import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { 
    Chart as ChartJS, 
    CategoryScale, 
    LinearScale, 
    BarElement, 
    LineElement, 
    PointElement, 
    ArcElement, 
    Title, 
    Tooltip, 
    Legend, 
    Filler 
} from 'chart.js';
import SuperAdminLayout from '../components/SuperAdminLayout';
import NetworkMap from '../components/NetworkMap';
import '../styles/superadmin.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler);

const BASE = 'https://menu.searchisend.in';

const SuperAnalytics = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('saas_superadmin_token');
    const userString = localStorage.getItem('saas_superadmin_user');
    
    // Tabs state: 'overview' | 'user_wise'
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [customersList, setCustomersList] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState('');
    
    const [filters, setFilters] = useState({
        date_filter: 'last_30_days',
        start_date: '',
        end_date: '',
        feature_filter: 'all'
    });

    const fetchAnalytics = async (silent = false) => {
        if (!silent) {
            setLoading(true);
            setError('');
        }
        try {
            const queryParams = new URLSearchParams(filters);
            if (activeTab === 'user_wise' && selectedCustomerId) {
                queryParams.append('customer_id', selectedCustomerId);
            }
            const query = queryParams.toString();
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_analytics&${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            
            if (data.success) {
                setStats(data.stats);
                if (data.customers_list) {
                    setCustomersList(data.customers_list);
                }
            } else {
                if (!silent) setError(data.message || 'Error fetching analytics');
            }
        } catch (err) {
            if (!silent) setError('Network error. Please try again.');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (!token || !userString) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        setStats(null);
        fetchAnalytics(false);
        // eslint-disable-next-line
    }, [activeTab, selectedCustomerId]);

    // Auto-fetch when filters change (except custom date partials)
    useEffect(() => {
        if (filters.date_filter !== 'custom') {
            fetchAnalytics(false);
        } else if (filters.date_filter === 'custom' && filters.start_date && filters.end_date) {
            fetchAnalytics(false);
        }
        // eslint-disable-next-line
    }, [filters.date_filter, filters.feature_filter, filters.start_date, filters.end_date]);

    // Set first customer as default when opening User-Wise tab for the first time
    useEffect(() => {
        if (activeTab === 'user_wise' && !selectedCustomerId && customersList.length > 0) {
            setSelectedCustomerId(customersList[0].id.toString());
        }
    }, [activeTab, customersList, selectedCustomerId]);

    // Background polling for real-time analytics updates every 10 seconds
    useEffect(() => {
        if (!token || !userString) return;

        const pollInterval = setInterval(() => {
            if (filters.date_filter !== 'custom' || (filters.date_filter === 'custom' && filters.start_date && filters.end_date)) {
                fetchAnalytics(true);
            }
        }, 10000);

        return () => clearInterval(pollInterval);
        // eslint-disable-next-line
    }, [activeTab, selectedCustomerId, filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // --- Chart Configurations ---
    const baseOptions = (title, displayLegend = false) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                display: displayLegend,
                labels: {
                    color: 'var(--text-primary)',
                    font: { family: 'Inter', size: 11 }
                }
            },
            title: {
                display: !!title,
                text: title,
                color: 'var(--text-primary)',
                font: { family: 'Inter', size: 14, weight: 'bold' }
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                borderColor: 'var(--border-color)',
                borderWidth: 1,
                titleFont: { size: 13, family: 'Inter', weight: 'bold' },
                bodyFont: { size: 12, family: 'Inter' },
                displayColors: true,
            }
        },
        scales: {
            y: { 
                beginAtZero: true, 
                ticks: { color: 'var(--text-secondary)', font: { family: 'Inter', size: 11 } }, 
                grid: { color: 'var(--border-color)', drawBorder: false } 
            },
            x: { 
                ticks: { color: 'var(--text-secondary)', font: { family: 'Inter', size: 10 } }, 
                grid: { display: false, drawBorder: false } 
            }
        },
    });

    const doughnutOptions = (title) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                display: true, 
                position: 'right',
                labels: {
                    color: 'var(--text-primary)',
                    font: { family: 'Inter', size: 11 }
                }
            },
            title: {
                display: !!title,
                text: title,
                color: 'var(--text-primary)',
                font: { family: 'Inter', size: 14, weight: 'bold' }
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                titleFont: { size: 13, family: 'Inter', weight: 'bold' },
                bodyFont: { size: 12, family: 'Inter' },
            }
        }
    });

    // 1. Overview: Customer Growth Chart
    const growthChartData = {
        labels: stats?.growth_labels || [],
        datasets: [
            {
                label: 'New Customers Added',
                data: stats?.growth_values || [],
                fill: true,
                backgroundColor: 'rgba(249, 115, 22, 0.15)',
                borderColor: 'var(--accent-primary)',
                borderWidth: 2,
                pointBackgroundColor: 'var(--accent-primary)',
                pointBorderColor: '#fff',
                tension: 0.4,
            },
        ],
    };

    // 2. Overview: Revenue Trend Chart
    const revenueChartData = {
        labels: stats?.revenue_labels || [],
        datasets: [
            {
                label: 'SaaS Revenue (₹)',
                data: stats?.revenue_values || [],
                fill: true,
                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                borderColor: 'var(--success)',
                borderWidth: 2,
                pointBackgroundColor: 'var(--success)',
                pointBorderColor: '#fff',
                tension: 0.4,
            },
        ],
    };

    // 3. Overview: Plan Distribution
    const planChartData = {
        labels: stats?.plan_labels || [],
        datasets: [
            {
                data: stats?.plan_values || [],
                backgroundColor: [
                    'rgba(249, 115, 22, 0.85)', // orange
                    'rgba(34, 197, 94, 0.85)',  // green
                    'rgba(59, 130, 246, 0.85)', // blue
                    'rgba(168, 85, 247, 0.85)', // purple
                ],
                borderColor: 'var(--bg-card)',
                borderWidth: 2,
            },
        ],
    };

    // 4. Overview: Feature Adoption Chart
    const featureChartData = {
        labels: ['Offer Popup', 'Coupon System', 'Push Notifications'],
        datasets: [
            {
                label: 'Adoption Count',
                data: [
                    stats?.feature_adoption?.popup || 0,
                    stats?.feature_adoption?.coupon || 0,
                    stats?.feature_adoption?.notification || 0,
                ],
                backgroundColor: 'rgba(59, 130, 246, 0.8)',
                borderColor: 'var(--info)',
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    // 5. User-Wise: Menu Views Trend
    const userViewChartData = {
        labels: stats?.view_trend_labels || [],
        datasets: [
            {
                label: 'Menu Views',
                data: stats?.view_trend_values || [],
                fill: true,
                backgroundColor: 'rgba(249, 115, 22, 0.15)',
                borderColor: 'var(--accent-primary)',
                borderWidth: 2,
                pointBackgroundColor: 'var(--accent-primary)',
                tension: 0.4,
            },
        ],
    };

    // 6. User-Wise: Menu Category Distribution
    const userCatChartData = {
        labels: stats?.cat_labels || [],
        datasets: [
            {
                label: 'Menu Items',
                data: stats?.cat_values || [],
                backgroundColor: 'rgba(168, 85, 247, 0.8)',
                borderColor: 'rgba(168, 85, 247, 1)',
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    // 7. SaaS Analytics: Trend Chart
    const saasTrendChartData = {
        labels: stats?.saas_stats?.trend_labels || [],
        datasets: [
            {
                label: 'Page Views',
                data: stats?.saas_stats?.trend_views || [],
                fill: true,
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderColor: 'var(--info)',
                borderWidth: 2,
                pointBackgroundColor: 'var(--info)',
                tension: 0.4,
            },
            {
                label: 'Unique Visitors',
                data: stats?.saas_stats?.trend_visitors || [],
                fill: true,
                backgroundColor: 'rgba(249, 115, 22, 0.1)',
                borderColor: 'var(--accent-primary)',
                borderWidth: 2,
                pointBackgroundColor: 'var(--accent-primary)',
                tension: 0.4,
            }
        ],
    };

    // 8. SaaS Analytics: Page Views Split
    const saasPageChartData = {
        labels: stats?.saas_stats?.page_labels || [],
        datasets: [
            {
                label: 'Page Views',
                data: stats?.saas_stats?.page_values || [],
                backgroundColor: 'rgba(249, 115, 22, 0.85)',
                borderColor: 'var(--accent-primary)',
                borderWidth: 1,
                borderRadius: 4,
            }
        ],
    };

    // 9. SaaS Analytics: Device Breakdown
    const saasDeviceChartData = {
        labels: stats?.saas_stats?.device_labels || [],
        datasets: [
            {
                data: stats?.saas_stats?.device_values || [],
                backgroundColor: [
                    'rgba(249, 115, 22, 0.85)',
                    'rgba(59, 130, 246, 0.85)',
                    'rgba(168, 85, 247, 0.85)',
                ],
                borderColor: 'var(--bg-card)',
                borderWidth: 2,
            }
        ],
    };

    // 10. SaaS Analytics: Platform Breakdown
    const saasPlatformChartData = {
        labels: stats?.saas_stats?.platform_labels || [],
        datasets: [
            {
                data: stats?.saas_stats?.platform_values || [],
                backgroundColor: [
                    'rgba(34, 197, 94, 0.85)',
                    'rgba(249, 115, 22, 0.85)',
                    'rgba(59, 130, 246, 0.85)',
                    'rgba(168, 85, 247, 0.85)',
                    'rgba(156, 163, 175, 0.85)',
                ],
                borderColor: 'var(--bg-card)',
                borderWidth: 2,
            }
        ],
    };

    return (
        <SuperAdminLayout>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                
                {/* Header */}
                <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                    <div>
                        <h2 className="sa-page-title">Analytics Dashboard</h2>
                        <p className="sa-page-subtitle">Monitor growth metrics, SaaS billing performance, and restaurant usage logs.</p>
                    </div>
                </div>

                {/* Filters Panel (Global controls at the top) */}
                <div className="sa-panel" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-end' }}>
                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <label className="sa-form-label"><i className="far fa-calendar-alt"></i> Time Period</label>
                            <select name="date_filter" className="sa-select" style={{ width: '100%', background: 'white' }} value={filters.date_filter} onChange={handleFilterChange}>
                                <option value="last_7_days">Last 7 Days</option>
                                <option value="last_30_days">Last 30 Days</option>
                                <option value="last_90_days">Last 90 Days</option>
                                <option value="all_time">All Time</option>
                                <option value="custom">Custom Range</option>
                            </select>
                        </div>

                        {filters.date_filter === 'custom' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1.5', minWidth: '300px' }}>
                                <div style={{ flex: 1 }}>
                                    <label className="sa-form-label">Start</label>
                                    <input type="date" name="start_date" className="sa-input" style={{ width: '100%', background: 'white' }} value={filters.start_date} onChange={handleFilterChange} />
                                </div>
                                <div style={{ marginTop: '25px', color: 'var(--text-secondary)', fontWeight: 500 }}>to</div>
                                <div style={{ flex: 1 }}>
                                    <label className="sa-form-label">End</label>
                                    <input type="date" name="end_date" className="sa-input" style={{ width: '100%', background: 'white' }} value={filters.end_date} onChange={handleFilterChange} />
                                </div>
                            </div>
                        )}

                        {activeTab === 'overview' && (
                            <div style={{ flex: '1', minWidth: '200px' }}>
                                <label className="sa-form-label"><i className="fas fa-filter"></i> Feature Adoption</label>
                                <select name="feature_filter" className="sa-select" style={{ width: '100%', background: 'white' }} value={filters.feature_filter} onChange={handleFilterChange}>
                                    <option value="all">All Customers</option>
                                    <option value="has_offer_popup">Has Offer Popup</option>
                                    <option value="has_coupon_system">Has Coupons</option>
                                    <option value="has_push_notification">Has Notifications</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tabs Panel switcher */}
                <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', marginBottom: '1.5rem', gap: '5px' }}>
                    <button 
                        onClick={() => setActiveTab('overview')} 
                        style={{
                            padding: '10px 20px', background: 'none', border: 'none', 
                            color: activeTab === 'overview' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                            borderBottom: activeTab === 'overview' ? '2px solid var(--accent-primary)' : 'none',
                            fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer'
                        }}
                    >
                        <i className="fas fa-chart-pie" style={{ marginRight: '8px' }}></i>Platform Overview
                    </button>
                    <button 
                        onClick={() => setActiveTab('user_wise')} 
                        style={{
                            padding: '10px 20px', background: 'none', border: 'none', 
                            color: activeTab === 'user_wise' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                            borderBottom: activeTab === 'user_wise' ? '2px solid var(--accent-primary)' : 'none',
                            fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer'
                        }}
                    >
                        <i className="fas fa-store" style={{ marginRight: '8px' }}></i>User-Wise Analytics
                    </button>
                    <button 
                        onClick={() => setActiveTab('saas_traffic')} 
                        style={{
                            padding: '10px 20px', background: 'none', border: 'none', 
                            color: activeTab === 'saas_traffic' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                            borderBottom: activeTab === 'saas_traffic' ? '2px solid var(--accent-primary)' : 'none',
                            fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer'
                        }}
                    >
                        <i className="fas fa-users" style={{ marginRight: '8px' }}></i>SaaS Visitor Analytics
                    </button>
                </div>

                {error && (
                    <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: '12px 16px', borderRadius: 'var(--radius-sm)', marginBottom: '20px', border: '1px solid var(--danger)', fontWeight: 500 }}>
                        <i className="fas fa-exclamation-circle"></i> {error}
                    </div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '5rem', color: 'var(--text-secondary)' }}>
                        <i className="fas fa-circle-notch fa-spin fa-3x" style={{ color: 'var(--accent-primary)', marginBottom: '1rem' }}></i>
                        <p style={{ fontWeight: 500 }}>Fetching latest statistics...</p>
                    </div>
                ) : stats && (
                    <>
                        {/* TAB 1: PLATFORM OVERVIEW */}
                        {activeTab === 'overview' && (
                            <>
                                {/* Stats Cards Grid */}
                                <section className="sa-stats-grid">
                                    <div className="sa-stat-card">
                                        <div className="sa-stat-label">Total Customers</div>
                                        <div className="sa-stat-value" style={{ color: 'var(--text-primary)' }}>{stats.total_customers}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                            <i className="fas fa-users" style={{ color: 'var(--accent-primary)', marginRight: '5px' }}></i> Registered Users
                                        </div>
                                    </div>
                                    <div className="sa-stat-card">
                                        <div className="sa-stat-label">Active Subscriptions</div>
                                        <div className="sa-stat-value" style={{ color: 'var(--success)' }}>{stats.active_customers}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                            <i className="fas fa-check-circle" style={{ color: 'var(--success)', marginRight: '5px' }}></i> Active Status
                                        </div>
                                    </div>
                                    <div className="sa-stat-card">
                                        <div className="sa-stat-label">Notification Subscribers</div>
                                        <div className="sa-stat-value" style={{ color: 'var(--info)' }}>{stats.total_subscribers}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                            <i className="fas fa-bell" style={{ color: 'var(--info)', marginRight: '5px' }}></i> Marketing Reach
                                        </div>
                                    </div>
                                    <div className="sa-stat-card">
                                        <div className="sa-stat-label">Revenue (Filtered Period)</div>
                                        <div className="sa-stat-value" style={{ color: 'var(--accent-primary)' }}>₹{Number(stats.filtered_revenue).toLocaleString('en-IN')}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                            <i className="fas fa-chart-line" style={{ color: 'var(--warning)', marginRight: '5px' }}></i> Billing History
                                        </div>
                                    </div>
                                </section>

                                {/* Visual Trend Charts */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                                    <div className="sa-panel" style={{ height: '400px', display: 'flex', flexDirection: 'column', padding: '1.5rem' }}>
                                        <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}><i className="fas fa-user-plus" style={{ color: 'var(--accent-primary)', marginRight: '8px' }}></i>New Customer Growth</h3>
                                        <div style={{ flex: 1, position: 'relative' }}>
                                            <Line data={growthChartData} options={baseOptions(null)} />
                                        </div>
                                    </div>

                                    <div className="sa-panel" style={{ height: '400px', display: 'flex', flexDirection: 'column', padding: '1.5rem' }}>
                                        <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}><i className="fas fa-hand-holding-usd" style={{ color: 'var(--success)', marginRight: '8px' }}></i>SaaS Billing Trends</h3>
                                        <div style={{ flex: 1, position: 'relative' }}>
                                            <Line data={revenueChartData} options={baseOptions(null)} />
                                        </div>
                                    </div>
                                </div>

                                {/* Plan Breakdowns */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
                                    <div className="sa-panel" style={{ display: 'flex', flexDirection: 'column', padding: '1.5rem' }}>
                                        <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}><i className="fas fa-tags" style={{ color: 'var(--accent-primary)', marginRight: '8px' }}></i>SaaS Plan Distribution</h3>
                                        <div style={{ height: '220px', position: 'relative' }}>
                                            <Doughnut data={planChartData} options={doughnutOptions(null)} />
                                        </div>
                                    </div>

                                    <div className="sa-panel" style={{ display: 'flex', flexDirection: 'column', padding: '1.5rem' }}>
                                        <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}><i className="fas fa-cogs" style={{ color: 'var(--info)', marginRight: '8px' }}></i>Key Feature Adoption</h3>
                                        <div style={{ height: '220px', position: 'relative' }}>
                                            <Bar data={featureChartData} options={baseOptions(null)} />
                                        </div>
                                    </div>

                                    <div className="sa-panel" style={{ display: 'flex', flexDirection: 'column', padding: '1.5rem' }}>
                                        <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}><i className="fas fa-wallet" style={{ color: 'var(--warning)', marginRight: '8px' }}></i>Platform Lifetime Revenue</h3>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.5rem', background: 'var(--bg-dark)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
                                            <div>
                                                <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '5px' }}>Grand Lifetime Revenue</div>
                                                <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-primary)' }}>₹{Number(stats.lifetime_revenue).toLocaleString('en-IN')}</div>
                                            </div>
                                            <i className="fas fa-award fa-3x" style={{ color: 'var(--warning)' }}></i>
                                        </div>
                                        <div style={{ marginTop: '1.5rem' }}>
                                            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '10px', fontSize: '0.9rem' }}>Popular Subscription Tiers (Sales)</div>
                                            {(stats.popular_plans?.length || 0) === 0 ? (
                                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>No orders recorded.</p>
                                            ) : (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                    {stats.popular_plans.map((p, i) => (
                                                        <span key={i} style={{ background: 'rgba(249, 115, 22, 0.1)', color: 'var(--accent-primary)', padding: '5px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600 }}>
                                                            {p.plan_name} ({p.purchase_count})
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* TAB 2: USER-WISE ANALYTICS */}
                        {activeTab === 'user_wise' && (
                            <div>
                                {/* Dropdown Selector */}
                                <div className="sa-panel" style={{ padding: '1.5rem', marginBottom: '1.5rem', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: '250px' }}>
                                        <label className="sa-form-label" style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                            <i className="fas fa-store" style={{ marginRight: '6px', color: 'var(--accent-primary)' }}></i>Select Restaurant for Deep Dive Analysis
                                        </label>
                                        <select 
                                            className="sa-select" 
                                            value={selectedCustomerId} 
                                            onChange={(e) => setSelectedCustomerId(e.target.value)}
                                            style={{ width: '100%', background: 'white' }}
                                        >
                                            <option value="">-- Select Restaurant --</option>
                                            {customersList.map(cust => (
                                                <option key={cust.id} value={cust.id}>
                                                    {cust.restaurant_name} ({cust.username})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {!selectedCustomerId ? (
                                    <div className="sa-panel" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                        <i className="fas fa-store-slash fa-3x" style={{ marginBottom: '1rem', opacity: 0.5 }}></i>
                                        <h4>No Restaurant Selected</h4>
                                        <p style={{ margin: 0 }}>Please choose a restaurant from the dropdown list above to explore customer metrics.</p>
                                    </div>
                                ) : stats.profile ? (
                                    <>
                                        {/* User Details Header Row */}
                                        <div className="sa-panel" style={{ padding: '1.5rem', marginBottom: '1.5rem', borderLeft: '4px solid var(--accent-primary)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
                                                {stats.profile.logo_url && (
                                                    <img 
                                                        src={stats.profile.logo_url} 
                                                        alt="Logo" 
                                                        style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border-color)' }} 
                                                    />
                                                )}
                                                <div style={{ flex: 1 }}>
                                                    <h3 style={{ margin: '0 0 5px 0', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        {stats.profile.restaurant_name}
                                                        <span style={{ fontSize: '0.8rem', padding: '3px 10px', borderRadius: '10px', background: stats.profile.status === 'Active' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(107, 114, 128, 0.15)', color: stats.profile.status === 'Active' ? 'var(--success)' : 'var(--text-secondary)', fontWeight: 600 }}>
                                                            {stats.profile.status}
                                                        </span>
                                                    </h3>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                        <span><i className="far fa-user"></i> {stats.profile.username}</span>
                                                        <span><i className="far fa-envelope"></i> {stats.profile.email}</span>
                                                        <span><i className="fas fa-link"></i> menu.searchisend.in/{stats.profile.username}</span>
                                                        <span><i className="fas fa-layer-group"></i> Plan: <strong>{stats.profile.subscription_tier || 'Trial'}</strong></span>
                                                        {stats.profile.renewal_date && <span><i className="far fa-clock"></i> Renewal: {new Date(stats.profile.renewal_date).toLocaleDateString('en-IN')}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* User Performance Stats Cards */}
                                        <section className="sa-stats-grid" style={{ marginBottom: '1.5rem' }}>
                                            <div className="sa-stat-card">
                                                <div className="sa-stat-label">Menu Views (In Period)</div>
                                                <div className="sa-stat-value" style={{ color: 'var(--text-primary)' }}>{stats.period_views}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                                    <i className="fas fa-eye" style={{ color: 'var(--accent-primary)', marginRight: '5px' }}></i> Lifetime views: {stats.profile.total_views}
                                                </div>
                                            </div>
                                            <div className="sa-stat-card">
                                                <div className="sa-stat-label">Orders (In Period)</div>
                                                <div className="sa-stat-value" style={{ color: 'var(--success)' }}>{stats.period_orders}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                                    <i className="fas fa-shopping-basket" style={{ color: 'var(--success)', marginRight: '5px' }}></i> Customer billing logs
                                                </div>
                                            </div>
                                            <div className="sa-stat-card">
                                                <div className="sa-stat-label">Restaurant Billings (In Period)</div>
                                                <div className="sa-stat-value" style={{ color: 'var(--accent-primary)' }}>₹{Number(stats.period_revenue).toLocaleString('en-IN')}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                                    <i className="fas fa-money-bill-wave" style={{ color: 'var(--warning)', marginRight: '5px' }}></i> Total Restaurant Sales
                                                </div>
                                            </div>
                                            <div className="sa-stat-card">
                                                <div className="sa-stat-label">Notification Subscribers</div>
                                                <div className="sa-stat-value" style={{ color: 'var(--info)' }}>{stats.subscribers_count}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                                    <i className="fas fa-bell" style={{ color: 'var(--info)', marginRight: '5px' }}></i> Menu Push Subscribers
                                                </div>
                                            </div>
                                        </section>

                                        {/* User Charts */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                                            <div className="sa-panel" style={{ height: '350px', display: 'flex', flexDirection: 'column', padding: '1.5rem' }}>
                                                <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}><i className="fas fa-chart-line" style={{ color: 'var(--accent-primary)', marginRight: '8px' }}></i>Menu Views Trend</h3>
                                                <div style={{ flex: 1, position: 'relative' }}>
                                                    {(stats.view_trend_labels?.length || 0) === 0 ? (
                                                        <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                                                            No view logs found in this period.
                                                        </div>
                                                    ) : (
                                                        <Line data={userViewChartData} options={baseOptions(null)} />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="sa-panel" style={{ height: '350px', display: 'flex', flexDirection: 'column', padding: '1.5rem' }}>
                                                <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}><i className="fas fa-utensils" style={{ color: 'var(--info)', marginRight: '8px' }}></i>Menu Items per Category</h3>
                                                <div style={{ flex: 1, position: 'relative' }}>
                                                    {(stats.cat_labels?.length || 0) === 0 ? (
                                                        <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                                                            No items / categories set up.
                                                        </div>
                                                    ) : (
                                                        <Bar data={userCatChartData} options={baseOptions(null)} />
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Detailed lists */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '1.5rem' }}>
                                            {/* Left: Subscription Payments */}
                                            <div className="sa-panel" style={{ padding: '1.5rem' }}>
                                                <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}><i className="fas fa-history" style={{ color: 'var(--accent-primary)', marginRight: '8px' }}></i>SaaS Subscription History</h3>
                                                {(stats.payments?.length || 0) === 0 ? (
                                                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>No payment records found.</p>
                                                ) : (
                                                    <div className="sa-table-container" style={{ margin: 0, boxShadow: 'none', border: '1px solid var(--border-color)' }}>
                                                        <table className="sa-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Plan</th>
                                                                    <th>Amount</th>
                                                                    <th>Date</th>
                                                                    <th>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {stats.payments.map((p, idx) => (
                                                                    <tr key={idx}>
                                                                        <td style={{ fontWeight: 600 }}>{p.plan_name}</td>
                                                                        <td>₹{Number(p.amount).toLocaleString('en-IN')}</td>
                                                                        <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                                                            {new Date(p.created_at).toLocaleDateString('en-IN')}
                                                                        </td>
                                                                        <td>
                                                                            <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '8px', background: p.payment_status === 'Paid' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)', color: p.payment_status === 'Paid' ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                                                                                {p.payment_status}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Right: Top Selling Menu Items */}
                                            <div className="sa-panel" style={{ padding: '1.5rem' }}>
                                                <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}><i className="fas fa-fire" style={{ color: 'var(--warning)', marginRight: '8px' }}></i>Top Selling Items (Period)</h3>
                                                {(stats.top_items?.length || 0) === 0 ? (
                                                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>No orders logged in this period.</p>
                                                ) : (
                                                    <div className="sa-table-container" style={{ margin: 0, boxShadow: 'none', border: '1px solid var(--border-color)' }}>
                                                        <table className="sa-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Item Name</th>
                                                                    <th style={{ textAlign: 'center' }}>Qty</th>
                                                                    <th style={{ textAlign: 'right' }}>Revenue</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {stats.top_items.map((item, idx) => (
                                                                    <tr key={idx}>
                                                                        <td style={{ fontWeight: 600 }}>{item.item_name}</td>
                                                                        <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>{item.qty}</td>
                                                                        <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--accent-primary)' }}>₹{Number(item.total).toLocaleString('en-IN')}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        )}

                        {activeTab === 'saas_traffic' && (
                            <div>
                                <section className="sa-stats-grid" style={{ marginBottom: '1.5rem' }}>
                                    <div className="sa-stat-card">
                                        <div className="sa-stat-label">Unique Visitors</div>
                                        <div className="sa-stat-value" style={{ color: 'var(--text-primary)' }}>
                                            {stats.saas_stats?.unique_visitors ?? 0}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                            <i className="fas fa-eye" style={{ color: 'var(--accent-primary)', marginRight: '5px' }}></i> Unique Sessions
                                        </div>
                                    </div>
                                    <div className="sa-stat-card">
                                        <div className="sa-stat-label">Page Views</div>
                                        <div className="sa-stat-value" style={{ color: 'var(--info)' }}>
                                            {stats.saas_stats?.page_views ?? 0}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                            <i className="fas fa-file-alt" style={{ color: 'var(--info)', marginRight: '5px' }}></i> Total Hits
                                        </div>
                                    </div>
                                    <div className="sa-stat-card">
                                        <div className="sa-stat-label">SaaS Signups</div>
                                        <div className="sa-stat-value" style={{ color: 'var(--success)' }}>
                                            {stats.saas_stats?.signups ?? 0}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                            <i className="fas fa-user-plus" style={{ color: 'var(--success)', marginRight: '5px' }}></i> Registered Accounts
                                        </div>
                                    </div>
                                    <div className="sa-stat-card">
                                        <div className="sa-stat-label">Conversion Rate</div>
                                        <div className="sa-stat-value" style={{ color: 'var(--accent-primary)' }}>
                                            {stats.saas_stats?.unique_visitors > 0 
                                                ? ((stats.saas_stats.signups / stats.saas_stats.unique_visitors) * 100).toFixed(2) + '%' 
                                                : '0.00%'}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                            <i className="fas fa-percentage" style={{ color: 'var(--warning)', marginRight: '5px' }}></i> Signup Conversion
                                        </div>
                                    </div>
                                </section>

                                {/* 4. Feature Adoption & Network Map */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                                    <div className="sa-panel" style={{ padding: '1.5rem' }}>
                                        <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}>
                                            <i className="fas fa-magic" style={{ color: 'var(--accent-primary)', marginRight: '8px' }}></i>Feature Adoption
                                        </h3>
                                        <div style={{ height: '300px' }}>
                                            <Bar data={featureChartData} options={baseOptions()} />
                                        </div>
                                    </div>
                                    
                                    <div className="sa-panel" style={{ padding: 0, overflow: 'hidden' }}>
                                        <NetworkMap />
                                    </div>
                                </div>

                                <div className="sa-panel" style={{ height: '380px', display: 'flex', flexDirection: 'column', padding: '1.5rem', marginBottom: '1.5rem' }}>
                                    <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1rem', marginTop: 0 }}>
                                        <i className="fas fa-chart-line" style={{ color: 'var(--accent-primary)', marginRight: '8px' }}></i>Visitor & Page Views Trend
                                    </h3>
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        {(stats.saas_stats?.trend_labels?.length || 0) === 0 ? (
                                            <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                                                No traffic data found in this period.
                                            </div>
                                        ) : (
                                            <Line data={saasTrendChartData} options={baseOptions(null, true)} />
                                        )}
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                                    <div className="sa-panel" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
                                        <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1.5rem', marginTop: 0 }}>
                                            <i className="fas fa-filter" style={{ color: 'var(--accent-primary)', marginRight: '8px' }}></i>SaaS Registration Funnel
                                        </h3>
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                            {[
                                                { label: '1. Landed on Home Page', key: 'landing', color: '#3b82f6' },
                                                { label: '2. Visited Signup Route', key: 'signup_page', color: '#8b5cf6' },
                                                { label: '3. Started Form Wizard', key: 'signup_step1', color: '#a855f7' },
                                                { label: '4. Form Completed (Registered)', key: 'signup_completed', color: '#10b981' }
                                            ].map((step, idx, arr) => {
                                                const currentVal = stats.saas_stats?.funnel?.[step.key] ?? 0;
                                                const maxVal = stats.saas_stats?.funnel?.landing ?? 1;
                                                const pct = maxVal > 0 ? ((currentVal / maxVal) * 100).toFixed(0) : 0;
                                                
                                                let prevVal = idx > 0 ? stats.saas_stats?.funnel?.[arr[idx-1].key] ?? 0 : 0;
                                                let stepPct = prevVal > 0 ? ((currentVal / prevVal) * 100).toFixed(0) : 100;

                                                return (
                                                    <div key={idx} style={{ position: 'relative' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.9rem', fontWeight: 600 }}>
                                                            <span style={{ color: 'var(--text-primary)' }}>{step.label}</span>
                                                            <span style={{ color: 'var(--text-secondary)' }}>
                                                                {currentVal} users {idx > 0 && <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>({stepPct}% step rate)</span>}
                                                            </span>
                                                        </div>
                                                        <div style={{ height: '28px', background: 'var(--bg-dark)', borderRadius: '6px', overflow: 'hidden', display: 'flex', alignItems: 'center', border: '1px solid var(--border-color)' }}>
                                                            <div style={{ 
                                                                width: `${Math.max(pct, 2)}%`, 
                                                                height: '100%', 
                                                                background: step.color,
                                                                transition: 'width 0.8s ease-out',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'flex-end',
                                                                paddingRight: '10px',
                                                                color: '#fff',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 700
                                                            }}>
                                                                {pct}%
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="sa-panel" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
                                        <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1.5rem', marginTop: 0 }}>
                                            <i className="fas fa-file-alt" style={{ color: 'var(--info)', marginRight: '8px' }}></i>Top Viewed SaaS Pages
                                        </h3>
                                        <div style={{ flex: 1, position: 'relative', height: '250px' }}>
                                            {(stats.saas_stats?.page_labels?.length || 0) === 0 ? (
                                                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                                                    No page views recorded.
                                                </div>
                                            ) : (
                                                <Bar data={saasPageChartData} options={baseOptions(null)} />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
                                    <div className="sa-panel" style={{ display: 'flex', flexDirection: 'column', padding: '1.5rem' }}>
                                        <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1.5rem', marginTop: 0 }}>
                                            <i className="fas fa-mobile-alt" style={{ color: 'var(--accent-primary)', marginRight: '8px' }}></i>Device Types
                                        </h3>
                                        <div style={{ height: '220px', position: 'relative' }}>
                                            {(stats.saas_stats?.device_labels?.length || 0) === 0 ? (
                                                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                                                    No device data.
                                                </div>
                                            ) : (
                                                <Doughnut data={saasDeviceChartData} options={doughnutOptions(null)} />
                                            )}
                                        </div>
                                    </div>

                                    <div className="sa-panel" style={{ display: 'flex', flexDirection: 'column', padding: '1.5rem' }}>
                                        <h3 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '1.5rem', marginTop: 0 }}>
                                            <i className="fas fa-laptop" style={{ color: 'var(--info)', marginRight: '8px' }}></i>Operating Systems
                                        </h3>
                                        <div style={{ height: '220px', position: 'relative' }}>
                                            {(stats.saas_stats?.platform_labels?.length || 0) === 0 ? (
                                                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                                                    No platform data.
                                                </div>
                                            ) : (
                                                <Doughnut data={saasPlatformChartData} options={doughnutOptions(null)} />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </SuperAdminLayout>
    );
};

export default SuperAnalytics;
